/**
 * Branding context — Phase 32 white-label.
 *
 * Single source of truth for the active company's visual identity inside
 * the *logged-in* app (admin dashboard + driver interface). Mirrors the
 * branding shown on the public booking page so a tenant feels the same
 * platform end-to-end.
 *
 * Resolution flow:
 *   1. AuthContext sets `user`.
 *   2. We hit `GET /api/auth/company-branding` (always returns a complete
 *      bundle thanks to backend defaults).
 *   3. We expose {companyName, logoUrl, primaryColor, isDefault} and push
 *      `--brand-primary` onto :root so CSS can read it everywhere without
 *      a single component refactor (utility classes referencing the var).
 *   4. On logout / role swap we refetch (the swap inside the same tenant
 *      is a no-op; switching to another tenant updates everything).
 *
 * Defaults (unauthenticated visitors / tenant has no branding):
 *   - companyName  = "S.DRIVE"
 *   - logoUrl      = "/brand/sdrive-logo.png"
 *   - primaryColor = "#F59E0B" (amber-500)
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULTS = {
  companyName:  "S.DRIVE",
  logoUrl:      "/brand/sdrive-logo.png",
  primaryColor: "#F59E0B",
  isDefault:    true,
};

const BrandingContext = createContext(DEFAULTS);

/**
 * Apply the active brand color as a CSS variable so any component can use
 * it via `bg-[var(--brand-primary)]` or `text-[var(--brand-primary)]`
 * without requiring a global rebuild. Also tweak two derived shades.
 *
 * Phase 32.1 — soften extreme colors (very bright neon green, neon pink,
 * vivid yellow) for UI comfort. Pure white text on raw `#10B981` or
 * `#84CC16` strains the eye fast on a dark background. We compute a
 * slightly darker + lower-saturation shade for surfaces while keeping the
 * raw color available as `--brand-primary-raw` for accent dots, charts,
 * etc. that need maximum punch.
 */
function _hexToHsl(hex) {
  const m = /^#?([a-f\d]{3}|[a-f\d]{6})$/i.exec(hex || "");
  if (!m) return null;
  let s = m[1];
  if (s.length === 3) s = s.split("").map(c => c + c).join("");
  const r = parseInt(s.slice(0, 2), 16) / 255;
  const g = parseInt(s.slice(2, 4), 16) / 255;
  const b = parseInt(s.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, l = (max + min) / 2;
  let sat = 0;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h *= 60;
  }
  return { h, s: sat, l };
}

function _hslToHex(h, s, l) {
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(255 * c).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** Soften a color for UI surfaces:
 *  - pull saturation down to ≤ 75%   (kills the "neon" feel)
 *  - clamp lightness to 40-58%       (dark theme comfort)
 *  Untouched if the input is already calm.
 */
function softenForUi(rawHex) {
  const hsl = _hexToHsl(rawHex);
  if (!hsl) return rawHex;
  let { h, s, l } = hsl;
  if (s > 0.75) s = 0.75;
  if (l > 0.58) l = 0.55;
  if (l < 0.40) l = 0.42;
  return _hslToHex(h, s, l);
}

function applyCssVars(rawColor) {
  const root = document.documentElement;
  const ui = softenForUi(rawColor);
  root.style.setProperty("--brand-primary",       ui);          // softened — used by the CSS variable layer
  root.style.setProperty("--brand-primary-raw",   rawColor);    // exact tenant color — for charts/dots/legends
  root.style.setProperty("--brand-primary-hover", `color-mix(in srgb, ${ui} 88%, white)`);
  root.style.setProperty("--brand-primary-active",`color-mix(in srgb, ${ui} 88%, black)`);
  root.style.setProperty("--brand-primary-soft",  `color-mix(in srgb, ${ui} 14%, transparent)`);
}

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setBranding(DEFAULTS);
      applyCssVars(DEFAULTS.primaryColor);
      return;
    }
    try {
      const { data } = await api.get("/auth/company-branding");
      const next = {
        companyName:  data.company_name  || DEFAULTS.companyName,
        logoUrl:      data.logo_url      || DEFAULTS.logoUrl,
        primaryColor: data.primary_color || DEFAULTS.primaryColor,
        isDefault:    !!data.is_default,
        companyId:    data.company_id || null,
      };
      setBranding(next);
      applyCssVars(next.primaryColor);
    } catch {
      // Endpoint failure → keep S.DRIVE defaults so the UI never breaks.
      setBranding(DEFAULTS);
      applyCssVars(DEFAULTS.primaryColor);
    }
  }, [user?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Update the browser tab title so it reflects the tenant name when
  // logged in — small but very effective white-label signal.
  useEffect(() => {
    if (!branding?.companyName) return;
    document.title = branding.companyName === DEFAULTS.companyName
      ? "S.DRIVE Dispatch"
      : `${branding.companyName} · Dispatch`;
  }, [branding?.companyName]);

  // ─── Tenant-aware PWA / home-screen icons ─────────────────────────────
  // iOS Safari reads `<link rel="apple-touch-icon">` from the page DOM at
  // the moment the user taps "Add to Home Screen". Rewriting these tags
  // after the tenant is identified is therefore the most reliable way to
  // get the correct logo into a per-tenant home-screen shortcut without
  // requiring per-tenant domains. Android Chrome consumes the manifest;
  // we point it at our dynamic `/api/branding/manifest.webmanifest`
  // endpoint which serves the correct name + icon set per company.
  useEffect(() => {
    if (!branding?.logoUrl) return;
    // White-label safety net: NEVER assign the bundled S.DRIVE asset as
    // the home-screen icon for a tenant that hasn't uploaded their own
    // logo. The backend transparently falls back to that asset for
    // in-app rendering (so the UI never shows a broken image), but the
    // PWA icon must stay neutral until the tenant configures their
    // brand. Otherwise an Elite VTC user would land an S.DRIVE icon on
    // their iPhone home screen.
    const isSdriveBundledFallback = (branding.logoUrl || "").endsWith("/brand/sdrive-logo.png");
    const isDefaultTenant = (branding.companyId || "") === "sdrive";
    if (isSdriveBundledFallback && !isDefaultTenant) {
      return;
    }
    try {
      const head = document.head;
      // Remove any prior favicon / apple-touch-icon so we don't end up
      // with multiple <link>s racing each other.
      head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="apple-touch-icon-precomposed"]')
          .forEach(el => el.remove());
      // Inject the tenant logo as both icon and apple-touch-icon. Apple
      // expects PNG; modern devices accept any format the URL points to.
      const fav = document.createElement("link");
      fav.rel  = "icon";
      fav.href = branding.logoUrl;
      head.appendChild(fav);
      const apple = document.createElement("link");
      apple.id   = "apple-touch-icon";
      apple.rel  = "apple-touch-icon";
      apple.href = branding.logoUrl;
      head.appendChild(apple);
      // Repoint the manifest at the tenant-aware route so Android picks
      // up the tenant name + icons.
      const manifest = document.getElementById("webmanifest")
                    || head.querySelector('link[rel="manifest"]');
      if (manifest && branding.companyId) {
        manifest.href = `/api/branding/manifest.webmanifest?company_id=${encodeURIComponent(branding.companyId)}`;
      }
    } catch {
      /* DOM mutation may race in older browsers — non-critical. */
    }
  }, [branding?.logoUrl, branding?.companyId]);

  const value = useMemo(() => ({ ...branding, refresh }), [branding, refresh]);
  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
